import { Button, StarIcon, Input, Modal, Spinner } from '@ag1/ui';
import { useCallback, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import TextareaV2 from '@portal/shared/ui/TextareaV2';
import { isValidEmail } from '@portal/shared/utils/check/isValidEmail';
import clsx from 'clsx';
import { useMutationPostV53WrittenReview } from '@portal/state/atlas/postV53WrittenReview';
import { useAGZAccount } from '@portal/state/hooks/useAGZAccount';
import {
  V53FlavorOptionValue,
  ProductOptionValue,
  V53WriteReviewFormFields
} from '@portal/types/review';
import { showNotification } from '@portal/shared/ui/Notification';
import Select from '@portal/shared/ui/Select';
import { REVIEW_PRODUCT_OPTION_VALUE_TO_LABEL_MAP } from '@portal/shared/constants/review';
import {
  REVIEW_PRODUCT_FLAVOR_OPTION_LIST,
  REVIEW_PRODUCT_FLAVOR_OPTION_LIST_AGZ,
  REVIEW_PRODUCT_OPTION_LIST,
  SKU_TO_PRODUCT_FLAVOR_MAP
} from '../constants';

import styles from './WriteReviewModal.module.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const WriteReviewModal = ({ isOpen, onClose }: Props) => {
  const postV53WrittenReview = useMutationPostV53WrittenReview();

  const { control, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      productType: '',
      productFlavor: '',
      rating: 0,
      title: '',
      content: '',
      name: '',
      email: ''
    }
  });

  const {
    productType: productTypeValue,
    productFlavor: productFlavorValue,
    rating: ratingValue,
    title: titleValue,
    content: contentValue,
    name: nameValue,
    email: emailValue
  } = watch();

  const [currentHoveredRating, setCurrentHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFormDisabled = useMemo(() => {
    return (
      !productTypeValue.length ||
      (productTypeValue === 'ag1-next-gen' && !productFlavorValue) ||
      ratingValue === 0 ||
      (productTypeValue === 'ag-agz' && !productFlavorValue) ||
      !titleValue.length ||
      !contentValue.length ||
      !nameValue.length ||
      !emailValue.length ||
      !isValidEmail(emailValue)
    );
  }, [
    productTypeValue,
    productFlavorValue,
    ratingValue,
    titleValue,
    contentValue,
    nameValue,
    emailValue
  ]);

  const { isAgzGateEnabled: enableAGZ } = useAGZAccount();

  const onRatingHover = useCallback(
    (currentRatingValue: number) => {
      setCurrentHoveredRating(currentRatingValue);
    },
    [setCurrentHoveredRating]
  );

  const onRatingHoverReset = useCallback(() => {
    setCurrentHoveredRating(0);
  }, [setCurrentHoveredRating]);

  const onRatingClick = useCallback(
    (newRatingValue: number) => {
      setValue('rating', newRatingValue);
    },
    [setValue]
  );

  const onSubmit: SubmitHandler<V53WriteReviewFormFields> = useCallback(
    async ({ productType, productFlavor, name, rating, title, content, email }) => {
      setIsSubmitting(true);
      postV53WrittenReview.mutateAsync({
        postBody: {
          product_title:
            REVIEW_PRODUCT_OPTION_VALUE_TO_LABEL_MAP[productType as ProductOptionValue],
          sku: SKU_TO_PRODUCT_FLAVOR_MAP(
            productType as ProductOptionValue,
            productFlavor as V53FlavorOptionValue
          ),
          display_name: name,
          email,
          review_content: content,
          review_title: title,
          review_score: rating
        },
        onComplete: () => {
          onClose();
          setIsSubmitting(false);
          setValue('productType', '');
          setValue('productFlavor', '');
          setValue('rating', 0);
          setValue('title', '');
          setValue('content', '');
          setValue('name', '');
          setValue('email', '');
          showNotification('mutationSuccess', { title: 'Your review was submitted!' });
        },
        onFail: () => {
          setIsSubmitting(false);
          showNotification('mutationError', {
            title: 'There was an error submitting your review. Please try again later.'
          });
        }
      });
    },
    [postV53WrittenReview, onClose, setIsSubmitting, setValue]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} animation="slide">
      <Modal.Header className={styles.headerGroup}>
        <h3 className={styles.title}>Write a review</h3>
      </Modal.Header>
      <Modal.Content hasFooter>
        <div className={styles.formFieldGroup}>
          <div
            className={styles.formFieldRowContainer}
            style={{ marginBottom: productTypeValue === 'ag1-next-gen' ? '0' : '8px' }}>
            <h4 className={styles.formLabel}>What product would you like to review?</h4>
            <Controller
              control={control}
              name="productType"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Select Product"
                  // TODO(AGZ): remove filter when AGZ is released
                  options={
                    enableAGZ
                      ? REVIEW_PRODUCT_OPTION_LIST
                      : REVIEW_PRODUCT_OPTION_LIST.filter((option) => option.value !== 'ag-agz')
                  }
                  required
                />
              )}
            />
          </div>
          {(productTypeValue === 'ag1-next-gen' || productTypeValue === 'ag-agz') && (
            <div className={styles.formFieldRowContainer} style={{ marginBottom: '8px' }}>
              <h4 className={styles.formLabel}>
                {`What flavor(s) of ${
                  REVIEW_PRODUCT_OPTION_VALUE_TO_LABEL_MAP[productTypeValue as ProductOptionValue]
                } would you like to review?`}
              </h4>
              <Controller
                control={control}
                name="productFlavor"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select Flavor"
                    options={
                      productTypeValue === 'ag-agz'
                        ? REVIEW_PRODUCT_FLAVOR_OPTION_LIST_AGZ.filter(
                            (option) => option.value !== 'all'
                          )
                        : REVIEW_PRODUCT_FLAVOR_OPTION_LIST.filter(
                            (option) => option.value !== 'all'
                          )
                    }
                  />
                )}
              />
            </div>
          )}
          <div className={styles.formFieldRowContainer}>
            <h4 className={styles.formLabel}>How would you rate this product?</h4>
            <div className={styles.ratingsContainer}>
              {[1, 2, 3, 4, 5].map((currentRating) => (
                <Button
                  key={currentRating}
                  className={clsx(
                    styles.ratingStar,
                    currentRating <= currentHoveredRating && styles.isHovered
                  )}
                  icon={
                    <StarIcon
                      size={24}
                      fill={currentRating <= ratingValue ? '#46DE46' : '#E0E2E2'}
                      thin
                    />
                  }
                  variant="link"
                  onMouseEnter={() => onRatingHover(currentRating)}
                  onMouseLeave={() => onRatingHoverReset()}
                  onClick={() => onRatingClick(currentRating)}
                  onlyIcon
                />
              ))}
            </div>
          </div>
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <Input {...field} className={styles.formField} placeholder="Title*" />
            )}
          />
          <Controller
            control={control}
            name="content"
            render={({ field }) => (
              <TextareaV2 {...field} className={styles.formField} placeholder="Review*" />
            )}
          />
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input {...field} className={styles.formField} placeholder="Name*" />
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Input {...field} className={styles.formField} placeholder="Email*" />
            )}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          className={styles.submitButton}
          theme="new"
          onClick={handleSubmit(onSubmit)}
          disabled={isFormDisabled || isSubmitting}
          size={{
            xxs: 'medium'
          }}
          rounded>
          {isSubmitting ? (
            <Spinner size={24} color="brand-white" withOverlay={false} />
          ) : (
            'Submit Review'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
